import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Save, Plus, Trash2, ExternalLink, Upload, GripVertical, ArrowUp, ArrowDown, Image as ImageIcon, Pencil, Rocket, Undo2, Archive, ArchiveRestore, Search } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, formatDate } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const MEDIA_CATEGORIES = ["Hero","Wedding","Engagement","Akad","Makeup","Decoration","Historia","Attire","Musical","Studio","Handcraft","Testimonial","Promo","Other"];

const SECTION_KEYS = [
  { key: "hero", label: "Hero Banner", desc: "Foto utama halaman" },
  { key: "wedding", label: "Wedding", desc: "Section paket wedding" },
  { key: "engagement", label: "Engagement", desc: "Section engagement / lamaran" },
  { key: "akad", label: "Akad", desc: "Section akad nikah" },
  { key: "makeup", label: "Makeup & Bridal", desc: "MUA Dessy Wedding" },
  { key: "decoration", label: "Decoration", desc: "Aesthetic Decoration" },
  { key: "historia", label: "Historia", desc: "Project by Historia" },
  { key: "attire", label: "Attire", desc: "Historia Attire" },
  { key: "musical", label: "Musical", desc: "Historia Musical" },
];

const empty = {
  hero_title: "", hero_subtitle: "", hero_tagline: "",
  cta_wa_message: "", contact_wa: "", contact_ig: "", contact_tiktok: "",
  promo_title: "", promo_body: "",
  testimonials: [], gallery_urls: [],
  hero_image_id: null, hero_image_mobile_id: null,
  section_images: {}, package_images: {},
};

export default function LandingCMS() {
  const [form, setForm] = useState(empty);
  const [live, setLive] = useState(empty);
  const [hasUnpublished, setHasUnpublished] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [packages, setPackages] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/cms/landing");
      const l = { ...empty, ...data, testimonials: data.testimonials || [], gallery_urls: data.gallery_urls || [],
                  section_images: data.section_images || {}, package_images: data.package_images || {} };
      setLive(l);
      // apply draft on top for editing
      const withDraft = { ...l, ...(data.draft || {}) };
      setForm(withDraft);
      setHasUnpublished(Boolean(data.has_unpublished));
      setPublishedAt(data.published_at || data.updated_at || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    api.get("/packages").then(({ data }) => setPackages(data || [])).catch(() => {});
  }, []);

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put("/cms/landing?mode=draft", stripInternal(form));
      toast.success("Draft tersimpan");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.put("/cms/landing?mode=publish", stripInternal(form));
      toast.success("Perubahan telah dipublikasikan ke publik ✨");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const discardDraft = async () => {
    if (!window.confirm("Buang semua perubahan draft dan kembali ke versi live?")) return;
    setBusy(true);
    try {
      await api.post("/cms/landing/discard-draft");
      toast.success("Draft dibatalkan");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="landing-cms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Landing Page Content</h1>
          <div className="flex items-center gap-2 mt-1 text-xs text-[#5C6066]">
            <span>Terakhir dipublikasikan: {publishedAt ? new Date(publishedAt).toLocaleString("id-ID") : "—"}</span>
            {hasUnpublished && <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50 text-[10px]">Ada perubahan draft</Badge>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href="/catalog" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline" data-testid="preview-landing">
            <ExternalLink className="w-4 h-4" /> Preview Live
          </a>
          {hasUnpublished && (
            <Button variant="outline" onClick={discardDraft} disabled={busy} data-testid="btn-discard-draft">
              <Undo2 className="w-4 h-4 mr-1.5"/> Buang Draft
            </Button>
          )}
          <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="btn-save-cms">
            <Save className="w-4 h-4 mr-1.5" /> {busy ? "Menyimpan..." : "Simpan Draft"}
          </Button>
          <Button onClick={publish} disabled={busy} className="bg-brand text-white" data-testid="btn-publish-cms">
            <Rocket className="w-4 h-4 mr-1.5" /> Publish
          </Button>
        </div>
      </div>

      <Tabs defaultValue="homepage" className="space-y-4" data-testid="cms-tabs">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="homepage" data-testid="tab-homepage">Homepage</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Media Library</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
          <TabsTrigger value="publish" data-testid="tab-publish">Preview & Publish</TabsTrigger>
        </TabsList>

        <TabsContent value="homepage">
          <HomepageTab form={form} setForm={setForm}/>
        </TabsContent>
        <TabsContent value="media">
          <MediaLibraryTab/>
        </TabsContent>
        <TabsContent value="sections">
          <SectionsTab form={form} setForm={setForm}/>
        </TabsContent>
        <TabsContent value="packages">
          <PackagesTab form={form} setForm={setForm} packages={packages}/>
        </TabsContent>
        <TabsContent value="publish">
          <PublishTab form={form} live={live} hasUnpublished={hasUnpublished} onPublish={publish} busy={busy}/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const stripInternal = (f) => {
  const { has_unpublished, published_at, draft, updated_at, updated_by, id, ...rest } = f;
  return rest;
};

// ─── Homepage Tab ───
function HomepageTab({ form, setForm }) {
  const addTestimonial = () => setForm({ ...form, testimonials: [...form.testimonials, { name: "", quote: "", rating: 5 }] });
  const removeT = (i) => setForm({ ...form, testimonials: form.testimonials.filter((_, idx) => idx !== i) });
  const updateT = (i, k, v) => {
    const arr = [...form.testimonials]; arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, testimonials: arr });
  };
  const reorderT = (from, to) => {
    if (from === to || to < 0 || to >= form.testimonials.length) return;
    const arr = [...form.testimonials]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    setForm({ ...form, testimonials: arr });
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
        <div className="font-display font-semibold border-b border-[#F0EBE0] pb-2">Hero Section</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Judul Utama" k="hero_title" form={form} setForm={setForm}/>
          <F label="Sub-judul" k="hero_subtitle" form={form} setForm={setForm}/>
          <div className="md:col-span-2">
            <Label>Tagline</Label>
            <Textarea rows={2} className="mt-1.5" value={form.hero_tagline}
              onChange={(e) => setForm({ ...form, hero_tagline: e.target.value })} data-testid="cms-hero_tagline"/>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#F0EBE0]">
          <MediaPicker label="Hero Image (Desktop)" value={form.hero_image_id}
            onChange={(id) => setForm({ ...form, hero_image_id: id })} category="Hero" testid="hero-image-desktop"/>
          <MediaPicker label="Hero Image (Mobile, opsional)" value={form.hero_image_mobile_id}
            onChange={(id) => setForm({ ...form, hero_image_mobile_id: id })} category="Hero" testid="hero-image-mobile"/>
        </div>
      </Card>

      <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
        <div className="font-display font-semibold border-b border-[#F0EBE0] pb-2">Kontak & Sosial Media</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="WhatsApp (62xxx)" k="contact_wa" form={form} setForm={setForm}/>
          <F label="Instagram (tanpa @)" k="contact_ig" form={form} setForm={setForm}/>
          <F label="TikTok (tanpa @)" k="contact_tiktok" form={form} setForm={setForm}/>
        </div>
        <div>
          <Label>Pesan Template WhatsApp CTA</Label>
          <Textarea rows={2} className="mt-1.5" value={form.cta_wa_message}
            onChange={(e) => setForm({ ...form, cta_wa_message: e.target.value })} data-testid="cms-cta_wa_message"/>
        </div>
      </Card>

      <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
        <div className="font-display font-semibold border-b border-[#F0EBE0] pb-2">Promo</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="Judul Promo" k="promo_title" form={form} setForm={setForm}/>
          <div className="md:col-span-2">
            <Label>Isi Promo</Label>
            <Textarea rows={2} className="mt-1.5" value={form.promo_body}
              onChange={(e) => setForm({ ...form, promo_body: e.target.value })} data-testid="cms-promo_body"/>
          </div>
        </div>
      </Card>

      <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
        <div className="flex items-center justify-between border-b border-[#F0EBE0] pb-2">
          <div className="font-display font-semibold">Testimoni ({form.testimonials.length})</div>
          <Button size="sm" variant="outline" onClick={addTestimonial} data-testid="btn-add-testimonial"><Plus className="w-3.5 h-3.5 mr-1"/> Tambah</Button>
        </div>
        {form.testimonials.length === 0 && <div className="text-sm text-[#8C9096] text-center py-4">Belum ada testimoni.</div>}
        {form.testimonials.map((t, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-[#FAF6EE] rounded border border-[#E5E0D8]" data-testid={`testimonial-${i}`}>
            <div className="md:col-span-1 flex md:flex-col items-center justify-center gap-0.5 pt-6">
              <button type="button" onClick={() => reorderT(i, i - 1)} disabled={i === 0} className="text-[#8C9096] hover:text-brand disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5"/></button>
              <div className="text-[10px] font-mono text-[#8C9096]">#{i + 1}</div>
              <button type="button" onClick={() => reorderT(i, i + 1)} disabled={i === form.testimonials.length - 1} className="text-[#8C9096] hover:text-brand disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5"/></button>
            </div>
            <div className="md:col-span-3"><Label className="text-xs">Nama</Label><Input className="mt-1" value={t.name || ""} onChange={(e) => updateT(i, "name", e.target.value)}/></div>
            <div className="md:col-span-6"><Label className="text-xs">Kutipan</Label><Textarea rows={2} className="mt-1" value={t.quote || ""} onChange={(e) => updateT(i, "quote", e.target.value)}/></div>
            <div className="md:col-span-1"><Label className="text-xs">⭐</Label><Input type="number" min={1} max={5} className="mt-1" value={t.rating || 5} onChange={(e) => updateT(i, "rating", Number(e.target.value))}/></div>
            <div className="md:col-span-1 flex items-end"><Button size="sm" variant="ghost" onClick={() => removeT(i)} className="text-rose-600 h-9 w-9 p-0"><Trash2 className="w-4 h-4"/></Button></div>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── Sections Tab ───
function SectionsTab({ form, setForm }) {
  const setSec = (key, patch) => {
    const current = form.section_images?.[key] || {};
    setForm({ ...form, section_images: { ...form.section_images, [key]: { ...current, ...patch } } });
  };
  return (
    <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
      <div className="font-display font-semibold border-b border-[#F0EBE0] pb-2">Pemetaan Gambar per Section</div>
      <p className="text-xs text-[#5C6066]">Setiap section landing page dapat menggunakan gambar khusus dari Media Library. Kosongkan untuk gunakan default.</p>
      <div className="space-y-3">
        {SECTION_KEYS.map((s) => {
          const cur = form.section_images?.[s.key] || {};
          return (
            <div key={s.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded border border-[#E5E0D8] bg-[#FAF6EE]" data-testid={`section-${s.key}`}>
              <div>
                <div className="font-semibold text-sm">{s.label}</div>
                <div className="text-[11px] text-[#8C9096] mt-0.5">{s.desc}</div>
                <div className="mt-2">
                  <Label className="text-[10px] uppercase">Focal</Label>
                  <select className="mt-1 w-full h-9 text-xs border border-[#E5E0D8] bg-white rounded px-2"
                    value={cur.focal || "cover"} onChange={(e) => setSec(s.key, { focal: e.target.value })}
                    data-testid={`section-${s.key}-focal`}>
                    <option value="cover">Cover (crop)</option>
                    <option value="contain">Contain (fit)</option>
                    <option value="top">Cover top</option>
                    <option value="center">Cover center</option>
                    <option value="bottom">Cover bottom</option>
                  </select>
                </div>
              </div>
              <MediaPicker label="Desktop" value={cur.media_id} onChange={(id) => setSec(s.key, { media_id: id })} category={s.label} testid={`section-${s.key}-desktop`}/>
              <MediaPicker label="Mobile (opsional)" value={cur.mobile_id} onChange={(id) => setSec(s.key, { mobile_id: id })} category={s.label} testid={`section-${s.key}-mobile`}/>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Packages Tab ───
function PackagesTab({ form, setForm, packages }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => packages.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())), [packages, q]);
  const setPkg = (name, patch) => {
    const cur = form.package_images?.[name] || {};
    setForm({ ...form, package_images: { ...form.package_images, [name]: { ...cur, ...patch } } });
  };
  return (
    <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3 border-b border-[#F0EBE0] pb-2">
        <div className="font-display font-semibold">Gambar per Paket ({packages.length})</div>
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari paket..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white h-9 text-sm" data-testid="pkg-search"/>
        </div>
      </div>
      <p className="text-xs text-[#5C6066]">Pilih gambar untuk setiap paket. Muncul sebagai cover di katalog landing page.</p>
      <div className="space-y-2">
        {filtered.map((p) => {
          const cur = form.package_images?.[p.name] || {};
          return (
            <div key={p.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 rounded border border-[#E5E0D8]" data-testid={`pkg-row-${p.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{p.name}</div>
                <div className="text-[11px] text-[#8C9096]">{p.category || "-"}</div>
              </div>
              <MediaPicker label="Desktop" value={cur.media_id} onChange={(id) => setPkg(p.name, { media_id: id })} category={null} testid={`pkg-${p.id}-desktop`}/>
              <MediaPicker label="Mobile (opsional)" value={cur.mobile_id} onChange={(id) => setPkg(p.name, { mobile_id: id })} category={null} testid={`pkg-${p.id}-mobile`}/>
              <div>
                <Label className="text-[10px] uppercase">Focal</Label>
                <select className="mt-1 w-full h-9 text-xs border border-[#E5E0D8] bg-white rounded px-2"
                  value={cur.focal || "cover"} onChange={(e) => setPkg(p.name, { focal: e.target.value })}
                  data-testid={`pkg-${p.id}-focal`}>
                  <option value="cover">Cover</option>
                  <option value="top">Cover top</option>
                  <option value="center">Cover center</option>
                  <option value="bottom">Cover bottom</option>
                  <option value="contain">Contain</option>
                </select>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Publish Tab ───
function PublishTab({ form, live, hasUnpublished, onPublish, busy }) {
  return (
    <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
      <div className="font-display font-semibold">Preview & Publish</div>
      <div className={`p-3 rounded border ${hasUnpublished ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="font-semibold text-sm">
          {hasUnpublished ? "🟡 Anda punya perubahan draft yang belum dipublikasikan" : "🟢 Semua perubahan sudah live"}
        </div>
        <div className="text-xs mt-1 text-[#5C6066]">
          {hasUnpublished
            ? "Klik Publish untuk menerapkan draft ke halaman publik."
            : "Konten public /catalog identik dengan editor. Buat perubahan di tab lain lalu Publish."}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><b>Hero title (live):</b> {live.hero_title || "—"}</div>
        <div><b>Hero title (draft):</b> {form.hero_title || "—"}</div>
        <div><b>Promo (live):</b> {live.promo_title || "—"}</div>
        <div><b>Promo (draft):</b> {form.promo_title || "—"}</div>
        <div><b>Testimoni (live):</b> {live.testimonials?.length || 0} item</div>
        <div><b>Testimoni (draft):</b> {form.testimonials?.length || 0} item</div>
      </div>
      <div className="flex gap-2">
        <a href="/catalog" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline">
          <ExternalLink className="w-4 h-4" /> Buka /catalog di tab baru
        </a>
      </div>
      <Button onClick={onPublish} disabled={busy || !hasUnpublished} className="bg-brand text-white" data-testid="btn-publish-cms-tab">
        <Rocket className="w-4 h-4 mr-1.5"/> {busy ? "Mempublikasikan..." : "Publish Perubahan"}
      </Button>
    </Card>
  );
}

// ─── Media Library Tab ───
function MediaLibraryTab() {
  const [items, setItems] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [uploadCat, setUploadCat] = useState("Other");

  const load = async () => {
    try {
      const { data } = await api.get("/cms/media", { params: {
        archived: archived ? "true" : "false",
        category: filterCat === "all" ? undefined : filterCat,
      } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, archived]);

  const filtered = useMemo(() => items.filter((it) => !q || (it.label || "").toLowerCase().includes(q.toLowerCase())), [items, q]);

  const handleUpload = async (files) => {
    for (const f of Array.from(files)) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: bukan gambar`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: max 8MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/cms/landing/gallery/upload?image_category=${encodeURIComponent(uploadCat)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${f.name} berhasil diupload`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
    load();
  };

  const archive = async (id) => {
    try { await api.delete(`/cms/media/${id}`); toast.success("Media diarsipkan"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const restore = async (id) => {
    try { await api.post(`/cms/media/${id}/restore`); toast.success("Media dipulihkan"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3 border-b border-[#F0EBE0] pb-3">
        <div className="font-display font-semibold">Media Library ({items.length})</div>
        <div className="flex items-center gap-2 flex-wrap">
          <select className="h-9 text-xs border border-[#E5E0D8] bg-white rounded px-2" value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} data-testid="media-upload-category">
            {MEDIA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 cursor-pointer bg-brand text-white px-3 py-2 rounded text-xs font-medium hover:bg-brand/90" data-testid="btn-media-upload-label">
            <Upload className="w-3.5 h-3.5"/> Upload
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files?.length && handleUpload(e.target.files)} data-testid="media-file-input"/>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select className="h-9 text-xs border border-[#E5E0D8] bg-white rounded px-2" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="media-filter-category">
          <option value="all">Semua Kategori</option>
          {MEDIA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="text-xs inline-flex items-center gap-1.5"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} data-testid="media-toggle-archived"/> Tampilkan diarsipkan</label>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C9096]" />
          <Input placeholder="Cari label..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 text-sm bg-white" data-testid="media-search"/>
        </div>
      </div>

      {filtered.length === 0 && <div className="text-sm text-[#8C9096] text-center py-10">Belum ada media. Upload untuk memulai.</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filtered.map((it) => (
          <div key={it.id} className="group relative rounded overflow-hidden border border-[#E5E0D8] bg-[#FAF6EE]" data-testid={`media-${it.id}`}>
            <img src={resolveUrl(it.url)} alt={it.alt_text || it.label}
              className="w-full h-32 object-cover"
              style={{ objectFit: it.focal === "contain" ? "contain" : "cover", objectPosition: ["top","center","bottom"].includes(it.focal) ? it.focal : "center" }}
              loading="lazy" onError={(e) => { e.target.style.opacity = "0.3"; }}/>
            <div className="absolute inset-x-0 top-0 flex items-center justify-between p-1.5 bg-gradient-to-b from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <Badge variant="outline" className="text-[9px] bg-white border-white">{it.image_category}</Badge>
              <div className="flex gap-1">
                <button onClick={() => setEditing(it)} className="p-1 rounded bg-white/90 hover:bg-white" title="Edit" data-testid={`media-edit-${it.id}`}>
                  <Pencil className="w-3 h-3"/>
                </button>
                {archived ? (
                  <button onClick={() => restore(it.id)} className="p-1 rounded bg-white/90 hover:bg-white" title="Restore" data-testid={`media-restore-${it.id}`}>
                    <ArchiveRestore className="w-3 h-3"/>
                  </button>
                ) : (
                  <button onClick={() => archive(it.id)} className="p-1 rounded bg-white/90 hover:bg-white text-rose-600" title="Archive" data-testid={`media-archive-${it.id}`}>
                    <Archive className="w-3 h-3"/>
                  </button>
                )}
              </div>
            </div>
            <div className="p-1.5 bg-white border-t border-[#F0EBE0]">
              <div className="text-[11px] font-medium truncate" title={it.label}>{it.label}</div>
              <div className="text-[9px] text-[#8C9096]">{Math.round(it.size / 1024)} KB</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="media-edit-modal">
          {editing && <MediaEditForm item={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)}/>}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MediaEditForm({ item, onSaved, onCancel }) {
  const [f, setF] = useState({ label: item.label || "", alt_text: item.alt_text || "",
    image_category: item.image_category || "Other", focal: item.focal || "cover",
    display_order: item.display_order || 0, active: item.active !== false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/cms/media/${item.id}`, f); toast.success("Media diperbarui"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Edit Media</DialogTitle><DialogDescription>{item.original_filename}</DialogDescription></DialogHeader>
      <img src={resolveUrl(item.url)} alt="" className="w-full h-40 object-cover rounded border border-[#E5E0D8]"
        style={{ objectFit: f.focal === "contain" ? "contain" : "cover", objectPosition: ["top","center","bottom"].includes(f.focal) ? f.focal : "center" }}/>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Label</Label><Input className="mt-1" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="edit-media-label"/></div>
        <div>
          <Label>Kategori</Label>
          <select className="mt-1 w-full h-10 text-sm border border-[#E5E0D8] bg-white rounded px-2" value={f.image_category} onChange={(e) => setF({ ...f, image_category: e.target.value })} data-testid="edit-media-category">
            {MEDIA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-2"><Label>Alt Text (untuk aksesibilitas & SEO)</Label>
          <Input className="mt-1" value={f.alt_text} onChange={(e) => setF({ ...f, alt_text: e.target.value })} data-testid="edit-media-alt"/></div>
        <div><Label>Focal</Label>
          <select className="mt-1 w-full h-10 text-sm border border-[#E5E0D8] bg-white rounded px-2" value={f.focal} onChange={(e) => setF({ ...f, focal: e.target.value })} data-testid="edit-media-focal">
            <option value="cover">Cover</option><option value="contain">Contain</option>
            <option value="top">Cover top</option><option value="center">Cover center</option><option value="bottom">Cover bottom</option>
          </select>
        </div>
        <div><Label>Urutan Tampil</Label><Input type="number" className="mt-1" value={f.display_order} onChange={(e) => setF({ ...f, display_order: Number(e.target.value) })}/></div>
        <div className="col-span-2 text-xs">
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })}/> Aktif</label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Batal</Button>
        <Button onClick={save} disabled={busy} className="bg-brand text-white" data-testid="btn-save-media-edit"><Save className="w-4 h-4 mr-1.5"/>{busy ? "..." : "Simpan"}</Button>
      </DialogFooter>
    </>
  );
}

// ─── Media Picker ───
function MediaPicker({ label, value, onChange, category, testid }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [filterCat, setFilterCat] = useState(category || "all");
  useEffect(() => {
    if (!open) return;
    api.get("/cms/media", { params: { archived: "false", category: filterCat === "all" ? undefined : filterCat } })
      .then(({ data }) => setItems(data)).catch(() => {});
  }, [open, filterCat]);
  const selected = items.find((it) => it.id === value);

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <button type="button" onClick={() => setOpen(true)} className="mt-1 w-full h-16 border border-dashed border-[#E5E0D8] bg-[#FBFAF6] rounded flex items-center gap-2 px-2 hover:bg-[#FAF6EE] text-left" data-testid={`picker-${testid}`}>
        {value ? (
          <>
            <img src={resolveUrl(`/api/public/cms/gallery/${value}`)} alt="" className="w-12 h-12 object-cover rounded border"/>
            <div className="flex-1 min-w-0"><div className="text-xs font-medium truncate">{selected?.label || value.slice(0,8)}</div><div className="text-[10px] text-[#8C9096]">Klik untuk ganti</div></div>
            <span onClick={(e) => { e.stopPropagation(); onChange(null); }} className="text-rose-500 text-xs hover:underline" data-testid={`picker-clear-${testid}`}>Hapus</span>
          </>
        ) : (
          <div className="flex-1 text-center text-xs text-[#8C9096]"><ImageIcon className="w-4 h-4 inline mr-1"/>Pilih gambar dari library</div>
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid={`picker-modal-${testid}`}>
          <DialogHeader><DialogTitle>Pilih Gambar — {label}</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <select className="h-9 text-xs border border-[#E5E0D8] bg-white rounded px-2" value={filterCat} onChange={(e) => setFilterCat(e.target.value)}>
              <option value="all">Semua Kategori</option>
              {MEDIA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="text-xs text-[#8C9096]">{items.length} media tersedia</div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3">
            {items.map((it) => (
              <button key={it.id} onClick={() => { onChange(it.id); setOpen(false); }}
                className={`rounded overflow-hidden border-2 hover:border-brand ${value === it.id ? "border-brand" : "border-[#E5E0D8]"}`}
                data-testid={`picker-item-${it.id}`}>
                <img src={resolveUrl(it.url)} alt={it.alt_text || it.label} className="w-full h-24 object-cover"
                  style={{ objectFit: it.focal === "contain" ? "contain" : "cover" }}/>
                <div className="p-1 bg-white text-[10px] truncate" title={it.label}>{it.label}</div>
              </button>
            ))}
            {items.length === 0 && <div className="col-span-full text-center text-sm text-[#8C9096] py-6">Tidak ada media di kategori ini. Upload dulu di tab Media Library.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, k, form, setForm, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={placeholder} data-testid={`cms-${k}`}/>
    </div>
  );
}
